import React, { useContext, useRef, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import {
  CodeTypeEnum,
  FeaturesEnum,
  ImportCompanyCodeListRequest,
  ImportSummary,
  ListCompanyCodeListFilter,
  ListCompanyCodeListItem,
  Pageable,
  PermissionTypesEnum,
} from '@api/ringil-api.v2.tsx';
import SvgId from '@icons/id.svg?react';
import SvgPlus from '@icons/plus.svg?react';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { ApiContext } from '@api/api.tsx';
import RingilDataTable, {
  ColumnConfig,
  FilterConfig,
  FilterType,
  PagedData,
} from '@components/RingilDataTable/RingilDataTable.tsx';
import { MassActionConfig } from '@components/RingilDataTable/RingilMassActions.tsx';
import SvgDelete from '@icons/delete-trash.svg?react';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import SecuredArea from '@components/security/SecuredArea';
import {
  useEditCompanyCatalogItemSidebar,
} from '@app/pages/customer/CompanyProfile/sidebar/EditCompanyCatalogItemSidebar.tsx';
import {
  useCreateMassEditCompanyCatalogItemsSidebar,
} from '@app/pages/customer/CompanyProfile/sidebar/MassEditCompanyCatalogItemsSidebar.tsx';
import SvgEdit from '@icons/pencil-inverted.svg?react';
import { VerticalDivider } from '@components/ringil3/Features/Attachments/ActionPanel.tsx';
import SvgImport from '@icons/file-inverted.svg?react';
import { useCreateInputPasteTabular } from '@components/framework/input/InputPasteTabular/InputPasteTabular.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import SvgAlert from '@icons/alert.svg?react';
import SvgSuccess from '@icons/check-circle.svg?react';

interface ComponentProps {
  companyId: string;
}

interface ImportCompanyCodeListItemKey {
  codeType: string;
  externalId: string;
  name: string;
  description: string;
}


const CompanyProfileCatalogs: React.FC<ComponentProps> = ({ companyId }) => {
  const { tr } = useTranslationLgs();
  const {
    listCompanyCodeList,
    deleteCompanyCodeList,
    importCompanyCodeList,
  } = useContext(ApiContext);

  const catalogsFlag = useSecuredFlag('manage-company-catalogs', {
    kind: 'visibility',
    description: 'Manage company catalogs',
    permissions: [PermissionTypesEnum.PERMISSION_ADMIN_CODELISTS],
  });
  const onBehalfOfFlag = useSecuredFlag('on-behalf-of-codetype', {
    kind: 'feature',
    description: 'Include ON_BEHALF_OF in catalog code types',
    features: [FeaturesEnum.RESERVATION_ON_BEHALF_OF],
  });

  const codeListEmailsFlag = useSecuredFlag('code-list-notification-emails-mass-edit', {
    kind: 'feature',
    description: 'Code list notification e-mails',
    features: [FeaturesEnum.CODE_LIST_NOTIFICATION_EMAILS],
  });

  const onBehalfOfEnabled = onBehalfOfFlag.granted;
  const availableCodeTypes = Object.values(CodeTypeEnum).filter(value => onBehalfOfEnabled || value !== CodeTypeEnum.ON_BEHALF_OF);
  const [catalogItemData, setCatalogItemData] = useState<ListCompanyCodeListItem>(null);
  const [importSummary, setImportSummary] = useState<ImportSummary>();
  const [massActionItems, setMassActionItems] = useState<ListCompanyCodeListItem[]>([]);
  const [massActionUpdatedCount, setMassActionUpdatedCount] = useState<number>();
  const {
    setVisible: setVisibleEditCompanyCatalogItemSidebar,
    EditCompanyCatalogItemSidebar,
  } = useEditCompanyCatalogItemSidebar();
  const {
    setVisible: setVisibleMassEditCompanyCatalogItemsSidebar,
    MassEditCompanyCatalogItemsSidebar,
  } = useCreateMassEditCompanyCatalogItemsSidebar();

  const {
    InputPasteTabular,
    setVisible: setVisibleImportTable,
  } = useCreateInputPasteTabular<ImportCompanyCodeListItemKey>(['codeType', 'externalId', 'name', 'description']);

  const handleImportSave = async (rows: ImportCompanyCodeListItemKey[]) => {
    const grouped = rows.reduce<Record<string, ImportCompanyCodeListItemKey[]>>((acc, row) => {
      if (!acc[row.codeType]) acc[row.codeType] = [];
      acc[row.codeType].push(row);
      return acc;
    }, {});

    setImportSummary({
      total: 0,
      created: 0,
      updated: 0,
      errors: 0,
    });

    for (const [codeType, items] of Object.entries(grouped)) {
      const request: ImportCompanyCodeListRequest = {
        items: items.map(({ externalId, name, description }) => ({
          externalId,
          name,
          description,
        })),
      };
      await new Promise<void>((resolve) => {
        importCompanyCodeList(
          codeType,
          request,
          data => {
            if (data.summary) {
              setImportSummary(prev => ({
                total: (prev?.total || 0) + (data.summary.total || 0),
                created: (prev?.created || 0) + (data.summary.created || 0),
                updated: (prev?.updated || 0) + (data.summary.updated || 0),
                errors: (prev?.errors || 0) + (data.summary.errors || 0),
              }));
            }
            resolve();
          },
        );
      });
    }
    tableRef?.current?.reloadData();
  };

  const tableRef = useRef<{
    reloadData: () => void,
    selectedObjects: ListCompanyCodeListItem[],
    clear: () => void
  }>(null);

  const loadDataApi = (filter: ListCompanyCodeListFilter, pageable: Pageable, onSuccess: (pagedData: PagedData<ListCompanyCodeListItem>) => void) => {
    listCompanyCodeList(
      filter,
      pageable,
      data => {
        onSuccess(data);
      },
    );
  };

  const renderTypeColumn = (rowData: ListCompanyCodeListItem) => {
    return (
      <div className={'flex flex-col items-start gap-2'}>
        <span>
          {tr(`CompanyProfileCatalogs.codeType.${rowData.codeType}`, rowData.codeType)}
        </span>
      </div>
    );
  };

  const renderExternalIdColumn = (rowData: ListCompanyCodeListItem) => {
    return (
      <div className={'flex flex-col items-start gap-2'}>
        <span>
          {rowData.externalId}
        </span>
      </div>
    );
  };

  const renderNameColumn = (rowData: ListCompanyCodeListItem) => {
    return (
      <div className={'flex flex-col items-start gap-2'}>
        <span>
          {rowData.name}
        </span>
      </div>
    );
  };

  const renderDescriptionColumn = (rowData: ListCompanyCodeListItem) => {
    return (
      <div className={'flex flex-col items-start gap-2'}>
        <span>
          {rowData.description}
        </span>
      </div>
    );
  };


  const renderActionsColumn = (rowData: ListCompanyCodeListItem) => {
    return (
      <div className={'flex flex-row items-center justify-start gap-2'}>
        <RingilButton
          mode={'table'}
          icon={<SvgDelete />}
          onClick={() => deleteCompanyCodeList(
            rowData.id,
            () => {
              tableRef?.current?.reloadData();
            },
          )}
        />
        <RingilButton
          mode={'table'}
          icon={<SvgEdit />}
          onClick={() => {
            setCatalogItemData(rowData);
            setVisibleEditCompanyCatalogItemSidebar(true);
          }}
        />
      </div>
    );
  };

  const columnsConfig: ColumnConfig<ListCompanyCodeListItem>[] = [
    {
      id: 'codeType',
      header: 'CompanyProfileCatalogs.columnHeaderCatalogsCodeType',
      headerDefault: 'Code type',
      width: '15%',
      sortable: false,
      field: 'codeType',
      order: 1,
      render: renderTypeColumn,
    },
    {
      id: 'externalId',
      header: 'CompanyProfileCatalogs.columnHeaderCatalogsExternalId',
      headerDefault: 'External ID',
      width: '10%',
      sortable: false,
      field: 'externalId',
      order: 2,
      render: renderExternalIdColumn,
    },
    {
      id: 'name',
      header: 'CompanyProfileCatalogs.columnHeaderCatalogsName',
      headerDefault: 'Name',
      width: '20%',
      sortable: false,
      field: 'roles',
      order: 3,
      render: renderNameColumn,
    },
    {
      id: 'description',
      header: 'CompanyProfileCatalogs.columnHeaderCatalogsDescription',
      headerDefault: 'Description',
      width: '45%',
      sortable: false,
      field: 'description',
      order: 4,
      render: renderDescriptionColumn,
    },
    {
      id: 'actions',
      header: 'CompanyProfileCatalogs.columnHeaderCatalogsActions',
      headerDefault: 'Actions',
      width: '10%',
      sortable: false,
      order: 5,
      render: renderActionsColumn,
    },
  ];

  const massActionConfig: MassActionConfig<ListCompanyCodeListItem>[] = [
    {
      label: tr('CompanyProfileCatalogs.massActionEditCostCenters', 'Edit cost centres'),
      icon: <SvgEdit />,
      onClick: () => {
        setMassActionUpdatedCount(undefined);
        setMassActionItems(tableRef.current?.selectedObjects || []);
        setVisibleMassEditCompanyCatalogItemsSidebar(true);
      },
      disabled: selected => selected.length < 2,
      disabledReason: () => tr('MassEditCompanyCatalogItemsSidebar.selectAtLeastTwo', 'Select at least two cost centres'),
    },
  ];

  const filtersConf: FilterConfig[] = [
    {
      filterType: FilterType.Dropdown,
      placeholder: tr(`CompanyProfileCatalogs.columnHeaderCatalogsCodeType`, `Code type`),
      value: ['codeType'],
      options: availableCodeTypes.map(value => ({
        label: tr(`CompanyProfileCatalogs.codeType.${value}`, value),
        value: value,
        active: true
      })),
    },
    {
      filterType: FilterType.InputText,
      placeholder: tr(`CompanyProfileCatalogs.columnHeaderCatalogsExternalId`, `External ID`),
      value: ['externalId'],
    },
    {
      filterType: FilterType.InputText,
      placeholder: tr(`CompanyProfileCatalogs.columnHeaderCatalogsName`, `Name`),
      value: ['name'],
    },
  ];


  return (
    <>
      <SecuredArea id="company-catalogs-section" label="Company catalogs section" description="The company's code lists" visible={catalogsFlag}>
        <>
          <div className="flex flex-row mb-2 items-center">
            <COHeadline
              variant={'h2'}
              title={tr('CompanyProfileCatalog.catalogsTitle', 'Catalogs')}
              icon={<SvgId className={'w-4 h-4 text-content-tertiary'} />}
            />
            <RingilButton
              icon={<SvgPlus />}
              label={tr(`CompanyProfileCatalog.addNewCatalogItem`, `Add new catalog item`)}
              mode={'link'}
              onClick={() => setVisibleEditCompanyCatalogItemSidebar(true)}
            />
            <VerticalDivider />
            <RingilButton
              label={tr(`CompanyProfileCatalog.newImport`, `New import`)}
              icon={<SvgImport />}
              mode={'link'}
              onClick={() => {
                setVisibleImportTable(true);
              }}
            />
            <a href={'/imports/importCatalogs.xlsx'}>{tr(`CompanyProfileCatalog.importTemplate`, `Download template`)}</a>

          </div>

          {importSummary && (importSummary.created > 0 || importSummary.updated > 0) && (
            <COInlineMessage
              variant={'success'}
              icon={<span className={'text-content-tertiary'}><SvgSuccess /></span>}
            >
             <span className={'text-content-primary'}>
               {tr('CompanyProfileCatalog.importSuccessMessage', 'Import was successful. We have created {{createCount}} entries. We have updated {{updatedCount}} entries', {
                 createCount: importSummary.created,
                 updatedCount: importSummary.updated,
               })}
             </span>
                </COInlineMessage>
              )}

          {importSummary && importSummary.errors > 0 && (
            <COInlineMessage
              variant={'error'}
              icon={<span className={'text-content-tertiary'}><SvgAlert /></span>}
            >
             <span className={'text-content-primary'}>
               {tr('CompanyProfileCatalog.importFailedMessage', 'Import failed for {{count}} items.', { count: importSummary.errors })}
             </span>
            </COInlineMessage>
          )}

          {massActionUpdatedCount > 0 && (
            <COInlineMessage
              variant={'success'}
              icon={<span className={'text-content-tertiary'}><SvgSuccess /></span>}
            >
             <span className={'text-content-primary'}>
               {tr('CompanyProfileCatalog.massActionSuccessMessage', 'Updated {{count}} cost centres.', { count: massActionUpdatedCount })}
             </span>
            </COInlineMessage>
          )}

          <RingilDataTable
            ref={tableRef}
            columnConfig={columnsConfig as ColumnConfig<unknown>[]}
            filtersConf={filtersConf}
            loadDataFromApi={loadDataApi}
            filterName={'company_profile_catalogs_list'}
            massActionConfig={codeListEmailsFlag.granted ? (massActionConfig as MassActionConfig<unknown>[]) : undefined}
            isRowSelectable={(row: unknown) => (row as ListCompanyCodeListItem).codeType === CodeTypeEnum.COST_CENTER}
          />
        </>
      </SecuredArea>

      <EditCompanyCatalogItemSidebar
        data={catalogItemData}
        onChanged={() => {
          setCatalogItemData(null);
          tableRef?.current?.reloadData();
        }}
      />

      <MassEditCompanyCatalogItemsSidebar
        items={massActionItems}
        onChanged={updatedCount => {
          setMassActionUpdatedCount(updatedCount);
          tableRef?.current?.clear();
          tableRef?.current?.reloadData();
        }}
      />

      <InputPasteTabular onSave={rows => {
        handleImportSave(rows);
      }} />
    </>
  );
};


export default CompanyProfileCatalogs;
